import React from "react";
import { cn } from "@/lib/utils";
import { PriceText, UsageChip } from "@/components/produkter/bits";
import { familiesOf, minText, VARIANT_ORDER, VARIANT_SHORT } from "@/lib/produkter";

const RATE_COLS = "minmax(0,1fr) 92px 92px 92px 92px";

/** Timepriser: one row per service, the four tariffs side by side – the way the price list is actually read. */
export function RateFamilies({ lines, selected, onOpen, mobile }) {
  const fams = familiesOf(lines);
  if (mobile) {
    return (
      <div data-testid="produkt-families">
        {fams.map((f) => (
          <button key={f.key} type="button" onClick={() => onOpen((f.variants.normal || f.lines[0]).id)} className={cn("flex w-full flex-col gap-1.5 border-b border-black/[0.05] py-3 pl-4 pr-3 text-left transition-colors hover:bg-black/[0.02]", f.lines.some((l) => l.id === selected) && "bg-[hsl(var(--navy)/0.04)]")} data-testid={`produkt-family-${f.key}`}>
            <span className="text-[14px] font-medium">{f.label}</span>
            <span className="flex flex-wrap gap-x-4 gap-y-1">{VARIANT_ORDER.filter((v) => f.variants[v]).map((v) => <span key={v} className="text-[12.5px] text-muted-foreground">{VARIANT_SHORT[v]} <PriceText line={f.variants[v]} size="sm" className="ml-0.5" /></span>)}</span>
          </button>
        ))}
      </div>
    );
  }
  return (
    <div data-testid="produkt-families">
      <div className="grid items-end gap-x-3 py-2 pl-4 pr-3 text-[11.5px] font-medium text-muted-foreground/70" style={{ gridTemplateColumns: RATE_COLS }}>
        <span>Tjeneste · pris pr. time</span>{VARIANT_ORDER.map((v) => <span key={v} className="text-right">{VARIANT_SHORT[v]}</span>)}
      </div>
      {fams.map((f) => {
        const sel = f.lines.some((l) => l.id === selected);
        return (
          <div key={f.key} role="row" className={cn("group relative grid items-center gap-x-3 border-t border-black/[0.05] pl-4 pr-3 transition-colors hover:bg-black/[0.02]", sel && "bg-[hsl(var(--navy)/0.04)]")} style={{ gridTemplateColumns: RATE_COLS }} data-testid={`produkt-family-${f.key}`}>
            {sel ? <span className="absolute inset-y-1.5 left-0 w-[3px] rounded-r bg-[hsl(var(--navy))]" /> : null}
            <button type="button" onClick={() => onOpen((f.variants.normal || f.lines[0]).id)} className="flex min-h-[44px] min-w-0 items-center gap-2.5 py-2 text-left outline-none focus-visible:underline" data-testid={`produkt-family-open-${f.key}`}>
              <span className="truncate text-[14px] font-medium text-foreground">{f.label}</span>
              {f.usage ? <UsageChip usage={{ cases: f.usage }} /> : null}
            </button>
            {VARIANT_ORDER.map((v) => f.variants[v] ? (
              <button key={v} type="button" onClick={() => onOpen(f.variants[v].id)} className={cn("flex h-[44px] items-center justify-end rounded-[6px] px-1.5 outline-none transition-colors hover:bg-black/[0.04] focus-visible:ring-2 focus-visible:ring-ring/40", selected === f.variants[v].id && "bg-[hsl(var(--navy)/0.08)]")} aria-label={`${f.label} – ${VARIANT_SHORT[v]}`} data-testid={`produkt-variant-${f.variants[v].id}`}>
                <PriceText line={{ ...f.variants[v], unit: null }} />
              </button>
            ) : <span key={v} className="text-right text-muted-foreground/40">–</span>)}
          </div>
        );
      })}
    </div>
  );
}

/** Any other group: description, usage, minimum, price. The whole row opens the line. */
export function LineRows({ lines, selected, onOpen }) {
  return (
    <div role="list" data-testid="produkt-lines">
      {lines.map((l) => {
        const sel = selected === l.id;
        const min = minText(l);
        return (
          <button key={l.id} type="button" role="listitem" onClick={() => onOpen(l.id)} className={cn("group relative flex w-full items-center gap-3 border-t border-black/[0.05] py-3 pl-4 pr-3 text-left outline-none transition-colors hover:bg-black/[0.02] focus-visible:bg-black/[0.03] focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring/30", sel && "bg-[hsl(var(--navy)/0.04)]")} data-testid={`produkt-line-${l.id}`} aria-current={sel ? "true" : undefined}>
            {sel ? <span className="absolute inset-y-1.5 left-0 w-[3px] rounded-r bg-[hsl(var(--navy))]" /> : null}
            <span className="min-w-0 flex-1">
              <span className="block truncate text-[14px] text-foreground">{l.description}</span>
              {l.note && l.price_kind === "note" ? <span className="mt-0.5 block truncate text-[12.5px] text-muted-foreground">{l.note}</span> : null}
            </span>
            <UsageChip usage={l.usage} className="hidden sm:inline-flex" />
            {min ? <span className="hidden shrink-0 text-[12px] text-muted-foreground md:inline">{min}</span> : null}
            <PriceText line={l} className="shrink-0 justify-end sm:min-w-[120px]" />
          </button>
        );
      })}
    </div>
  );
}
